
import { useState } from 'react';
import TopicCard from './TopicCard';

interface TopicListProps {
  selectedCategory: string;
  onCreateTopic: () => void;
}

const mockTopics = [
  {
    id: '1',
    title: 'Amazing Jazz Session - Miles Davis Deep Dive',
    content: 'Just finished an incredible 2-hour session exploring Miles Davis\' Kind of Blue album. The way he transitions between modal jazz concepts is absolutely mind-blowing...',
    author: {
      id: '1',
      name: 'Sarah Chen',
      avatar: 'https://public.readdy.ai/ai/img_res/a0b3acbda4a5eaa4ae410d81db0226af.jpg',
      isOnline: true,
      reputation: 1247
    },
    category: 'sessions',
    sessionId: 'session_123',
    createdAt: '2024-01-15T10:30:00Z',
    replies: 23,
    views: 156,
    likes: 45,
    isPinned: true,
    tags: ['jazz', 'miles-davis', 'modal-jazz', 'listening-session']
  },
  {
    id: '2',
    title: 'New Artist Discovery: Yussef Dayes',
    content: 'Found this incredible drummer/producer through the discovery algorithm. His blend of jazz, electronic, and Afrobeat is exactly what I\'ve been looking for...',
    author: {
      id: '2',
      name: 'Marcus Johnson',
      avatar: 'https://readdy.ai/api/search-image?query=young%20man%20with%20beard%20listening%20to%20music%2C%20modern%20headphones%2C%20cozy%20room%20setting%2C%20natural%20lighting%2C%20portrait%20photography&width=40&height=40&seq=user2&orientation=squarish',
      isOnline: false,
      reputation: 892
    },
    category: 'music-discovery',
    createdAt: '2024-01-15T08:45:00Z',
    replies: 18,
    views: 234,
    likes: 67,
    isPinned: false,
    tags: ['discovery', 'yussef-dayes', 'jazz-fusion', 'electronic']
  },
  {
    id: '3',
    title: 'Audio-Technica ATH-M50x vs Sony WH-1000XM4',
    content: 'Been comparing these two for critical listening sessions. The M50x has incredible detail for studio monitoring, but the Sony\'s noise cancellation...',
    author: {
      id: '3',
      name: 'Alex Rivera',
      avatar: 'https://readdy.ai/api/search-image?query=person%20testing%20audio%20equipment%20in%20professional%20studio%2C%20focused%20expression%2C%20high-end%20headphones%2C%20modern%20setup&width=40&height=40&seq=user3&orientation=squarish',
      isOnline: true,
      reputation: 1456
    },
    category: 'equipment',
    createdAt: '2024-01-15T07:20:00Z',
    replies: 31,
    views: 445,
    likes: 89,
    isPinned: false,
    tags: ['headphones', 'comparison', 'audio-technica', 'sony']
  },
  {
    id: '4',
    title: 'Weekly Community Listening Party - This Friday!',
    content: 'Join us for our weekly synchronized listening session this Friday at 8 PM EST. This week we\'re exploring the evolution of hip-hop production...',
    author: {
      id: '4',
      name: 'Community Bot',
      avatar: 'https://readdy.ai/api/search-image?query=friendly%20robot%20mascot%20with%20headphones%2C%20green%20and%20emerald%20color%20scheme%2C%20modern%20digital%20art%20style%2C%20welcoming%20expression&width=40&height=40&seq=bot1&orientation=squarish',
      isOnline: true,
      reputation: 999
    },
    category: 'community',
    createdAt: '2024-01-15T06:00:00Z',
    replies: 42,
    views: 678,
    likes: 123,
    isPinned: true,
    tags: ['community-event', 'listening-party', 'hip-hop', 'synchronized']
  },
  {
    id: '5',
    title: 'Ambient Session: Brian Eno\'s Music for Airports',
    content: 'Spent the entire morning with this masterpiece. The way Eno creates space and atmosphere is unmatched. Perfect for deep focus work...',
    author: {
      id: '5',
      name: 'Emma Thompson',
      avatar: 'https://readdy.ai/api/search-image?query=woman%20in%20peaceful%20meditation%20pose%20with%20ambient%20lighting%2C%20serene%20expression%2C%20minimalist%20background%2C%20soft%20natural%20light&width=40&height=40&seq=user5&orientation=squarish',
      isOnline: false,
      reputation: 734
    },
    category: 'sessions',
    sessionId: 'session_456',
    createdAt: '2024-01-14T22:15:00Z',
    replies: 15,
    views: 189,
    likes: 56,
    isPinned: false,
    tags: ['ambient', 'brian-eno', 'focus-music', 'atmospheric']
  },
  {
    id: '6',
    title: 'Best Budget Studio Monitors Under $200?',
    content: 'Setting up my first home studio and need recommendations for accurate monitors that won\'t break the bank. What are your experiences with...',
    author: {
      id: '6',
      name: 'David Kim',
      avatar: 'https://readdy.ai/api/search-image?query=young%20musician%20in%20home%20studio%20setup%2C%20surrounded%20by%20audio%20equipment%2C%20creative%20workspace%2C%20warm%20lighting%2C%20focused%20expression&width=40&height=40&seq=user6&orientation=squarish',
      isOnline: true,
      reputation: 445
    },
    category: 'equipment',
    createdAt: '2024-01-14T19:30:00Z',
    replies: 28,
    views: 356,
    likes: 72,
    isPinned: false,
    tags: ['studio-monitors', 'budget', 'home-studio', 'recommendations']
  }
];

export default function TopicList({ selectedCategory, onCreateTopic }: TopicListProps) {
  const [sortBy, setSortBy] = useState('recent');
  
  const filteredTopics = selectedCategory === 'all' 
    ? mockTopics 
    : mockTopics.filter(topic => topic.category === selectedCategory);

  const sortedTopics = [...filteredTopics].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    
    switch (sortBy) {
      case 'popular':
        return b.likes - a.likes;
      case 'replies':
        return b.replies - a.replies;
      default:
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedCategory === 'all' ? 'All Discussions' : 
             selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1).replace('-', ' ')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredTopics.length} topics • {filteredTopics.reduce((sum, topic) => sum + topic.replies, 0)} replies
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
          >
            <option value="recent">Most Recent</option>
            <option value="popular">Most Popular</option>
            <option value="replies">Most Replies</option>
          </select>
          
          <button
            onClick={onCreateTopic}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-add-line text-sm"></i>
            New Topic
          </button>
        </div>
      </div>

      {/* Topics */}
      <div className="space-y-4">
        {sortedTopics.map((topic) => (
          <TopicCard key={topic.id} topic={topic} />
        ))}
      </div>

      {/* Load More */}
      <div className="text-center pt-6">
        <button className="bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer whitespace-nowrap">
          Load More Topics
        </button>
      </div>
    </div>
  );
}
